export const API_BASE = import.meta.env.VITE_API_BASE || "/api/v1";

export type User = {
  id?: number | string;
  user_id?: number | string;
  full_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  token?: string;
};

export type FirebaseWebConfigResponse = {
  enabled: boolean;
  config?: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  detail?: string;
};

export type AddressData = {
  name?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
};

export type AddressRecord = {
  id: number;
  user_id: number;
  address_data: AddressData;
  type: string;
};

export type HeroContent = {
  eyebrow_text?: string;
  typewriter_texts?: string[];
  upload_title?: string;
  upload_subtitle?: string;
};

export type SlicerTooltip = {
  tooltip?: string;
  tooltip_image?: string;
};

export type SlicerColor = string | ({ name: string; hex?: string } & SlicerTooltip);

export type SlicerMaterial = SlicerTooltip & {
  name: string;
  colors: SlicerColor[];
};

export type SlicerOption = SlicerTooltip & {
  label: string;
  value: string;
  percent?: number;
};

export type SlicerOptions = {
  materials: SlicerMaterial[];
  infill_options: SlicerOption[];
  infill_patterns: SlicerOption[];
  support_styles: SlicerOption[];
  title_tooltips?: Array<SlicerTooltip & { title: string }>;
  support_default?: string;
  top_bottom_layers_default?: number;
};

export type Product = {
  id: number;
  name: string;
  subtitle?: string;
  description?: string;
  price: number | string;
  image?: string;
  stl_file?: string;
  color_options?: Array<{ label: string; hex?: string }>;
  is_active?: boolean;
};

export type Order = {
  id: number | string;
  display_id?: string;
  order_number?: string;
  status?: string;
  payment_status?: string;
  total_amount?: number | string;
  base_cost?: number | string;
  delivery_cost?: number | string;
  progress_percent?: number;
  progress_label?: string;
  created_at?: string;
  items?: unknown[];
  cart_items?: Array<Record<string, unknown>>;
  order_metadata?: Record<string, unknown>;
  delivery_address?: Record<string, unknown> | null;
  shipping?: Record<string, unknown>;
  weight_grams?: number | string;
  volume_cm3?: number | string;
  extra_requests?: string;
};

export type PaymentMethodsConfig = {
  cod_enabled?: boolean;
  razorpay_enabled?: boolean;
  razorpay_ready?: boolean;
  phonepe_enabled?: boolean;
  phonepe_ready?: boolean;
};

export type ShippingMethod = {
  method: string;
  label: string;
  amount: number;
  source?: string;
  fallback_eta?: string;
  safe_message?: string;
  courier_id?: string | number;
  courier_name?: string;
  estimated_delivery_days?: number | string;
  etd?: string;
};

export type TaxBreakdown = {
  enabled: boolean;
  tax_type: "cgst_sgst" | "igst" | "none";
  seller_state?: string;
  supply_state?: string;
  gst_rate: number;
  taxable_amount: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
  cgst: number;
  sgst: number;
  igst: number;
  total_tax: number;
  total: number;
};

export type ShippingRatesResponse = {
  methods: Record<string, ShippingMethod>;
  shipping_quote_source?: string;
  weight_kg?: number;
  dimensions?: number[];
  tax_by_method?: Record<string, TaxBreakdown>;
};

export type CartCheckoutPayload = {
  user_id: string | number;
  items: Array<Record<string, unknown>>;
  order_metadata?: Record<string, unknown>;
  delivery_address: Record<string, unknown>;
  billing_address?: Record<string, unknown>;
  billing_same?: boolean;
  save_shipping?: boolean;
  save_billing?: boolean;
  subtotal: number;
  shipping_method?: string;
  shipping_amount?: number;
  shipping_quote_source?: string;
  payment_method?: string;
  delivery?: number;
  total?: number;
};

export type CartCheckoutResponse = {
  order_id: number | string;
  display_id?: string;
  order_number?: string;
  status?: string;
  total?: number | string;
  shipping?: Record<string, unknown>;
};

export type PaymentInitResponse = {
  payment_id: number;
  provider: string;
  gateway_order_id: string;
  amount: number;
  currency: string;
  extra?: {
    pay_page_url?: string;
    key_id?: string;
    mock?: boolean;
    note?: string;
    [key: string]: unknown;
  };
};

export type PaymentVerifyResponse = {
  message?: string;
  status?: string;
  reason?: string;
};

export type QuoteRequest = {
  file: File;
  material: string;
  color: string;
  infill: string;
  support: string;
  supportStyle: string;
  infillPattern: string;
  nozzle: string;
  topBottomLayers: number;
  extraRequests: string;
  quantity: number;
  userId?: string | number | null;
};

export type QuoteResult = {
  filename: string;
  price: number;
  material: string;
  color: string;
  model_file_id?: number | null;
  stl_file?: string | null;
  volume?: number;
  weight?: number;
  quote_options?: Record<string, unknown>;
  raw?: Record<string, unknown>;
};

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type RequestOptions = RequestInit & {
  token?: string;
};

async function parseResponse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    const message =
      typeof data === "object" && data && "error" in data
        ? String((data as { error: unknown }).error)
        : typeof data === "object" && data && "detail" in data
          ? String((data as { detail: unknown }).detail)
        : typeof data === "object" && data && "reason" in data
          ? String((data as { reason: unknown }).reason)
        : typeof data === "object" && data && "message" in data
          ? String((data as { message: unknown }).message)
        : `Request failed with status ${response.status}`;
    throw new ApiError(message, response.status);
  }

  return data as T;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.token) headers.set("Authorization", `Bearer ${options.token}`);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers
  });

  return parseResponse<T>(response);
}

export function resolveAssetUrl(value?: string | null): string {
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  if (value.startsWith("/")) return value;
  return `${API_BASE.replace("/api/v1", "")}${value.startsWith("/") ? value : `/${value}`}`;
}

export async function getProducts(): Promise<Product[]> {
  return apiFetch<Product[]>("/products/list");
}

export async function getHeroContent(): Promise<HeroContent> {
  return apiFetch<HeroContent>("/hero-content");
}

export async function getSlicerOptions(): Promise<SlicerOptions> {
  return apiFetch<SlicerOptions>("/slicer-options");
}

export async function getProduct(id: string | number): Promise<Product> {
  return apiFetch<Product>(`/products/${id}`);
}

function normalizeLoginUser(data: User): User {
  return {
    id: data.id || data.user_id,
    user_id: data.user_id || data.id,
    full_name: data.full_name,
    email: data.email,
    phone: data.phone,
    role: data.role,
    token: data.token
  };
}

export async function requestEmailOtp(email: string): Promise<{ detail?: string }> {
  return apiFetch<{ detail?: string }>("/users/login/email", {
    method: "POST",
    body: JSON.stringify({ email })
  });
}

export async function verifyEmailOtp(email: string, otp: string): Promise<User> {
  const data = await apiFetch<User>("/users/login/email", {
    method: "POST",
    body: JSON.stringify({ email, otp })
  });
  return normalizeLoginUser(data);
}

export async function getFirebaseWebConfig(): Promise<FirebaseWebConfigResponse> {
  return apiFetch<FirebaseWebConfigResponse>("/users/firebase/config");
}

export async function loginWithFirebasePhone(phone: string, token: string): Promise<User> {
  const data = await apiFetch<User>("/users/login/otp", {
    method: "POST",
    body: JSON.stringify({ phone, token })
  });
  return normalizeLoginUser(data);
}

export async function exchangeGoogleCode(exchangeCode: string): Promise<User> {
  const data = await apiFetch<User>("/users/exchange", {
    method: "POST",
    body: JSON.stringify({ exchange_code: exchangeCode })
  });
  return normalizeLoginUser(data);
}

export async function getPaymentMethods(): Promise<PaymentMethodsConfig> {
  return apiFetch<PaymentMethodsConfig>("/payments/methods");
}

export async function getAddresses(userId: string | number, token: string): Promise<AddressRecord[]> {
  return apiFetch<AddressRecord[]>(`/users/addresses/${encodeURIComponent(String(userId))}`, { token });
}

export async function getProfile(userId: string | number, token: string): Promise<User> {
  return apiFetch<User>(`/users/profile/${encodeURIComponent(String(userId))}`, { token });
}

export async function updateProfile(
  userId: string | number,
  token: string,
  profile: Pick<User, "full_name" | "phone" | "email">
): Promise<User> {
  return apiFetch<User>(`/users/profile/${encodeURIComponent(String(userId))}`, {
    method: "PUT",
    token,
    body: JSON.stringify(profile)
  });
}

export async function createAddress(
  userId: string | number,
  token: string,
  addressData: AddressData,
  type: "shipping" | "billing" | "both"
): Promise<AddressRecord> {
  return apiFetch<AddressRecord>(`/users/address/${encodeURIComponent(String(userId))}`, {
    method: "POST",
    token,
    body: JSON.stringify({ address_data: addressData, type })
  });
}

export async function updateAddress(
  userId: string | number,
  token: string,
  addressId: string | number,
  addressData: AddressData,
  type: string
): Promise<AddressRecord> {
  return apiFetch<AddressRecord>(`/users/addresses/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(addressId))}`, {
    method: "PATCH",
    token,
    body: JSON.stringify({ address_data: addressData, type })
  });
}

export async function deleteAddress(userId: string | number, token: string, addressId: string | number): Promise<{ detail?: string }> {
  return apiFetch<{ detail?: string }>(
    `/users/addresses/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(addressId))}`,
    {
      method: "DELETE",
      token
    }
  );
}

export async function getShippingRates(
  items: Array<Record<string, unknown>>,
  subtotal: number,
  shippingAddress: AddressData,
  cod: boolean,
  token?: string
): Promise<ShippingRatesResponse> {
  return apiFetch<ShippingRatesResponse>("/shipping/rates", {
    method: "POST",
    token,
    body: JSON.stringify({
      items,
      subtotal,
      shipping_address: shippingAddress,
      cod
    })
  });
}

export async function getMyOrders(userId: string | number, token?: string): Promise<Order[]> {
  return apiFetch<Order[]>(`/orders/my-orders?user_id=${encodeURIComponent(String(userId))}`, { token });
}

export async function getOrderDetail(orderId: string | number, userId: string | number, token?: string): Promise<Order> {
  return apiFetch<Order>(`/orders/${encodeURIComponent(String(orderId))}?user_id=${encodeURIComponent(String(userId))}`, { token });
}

export async function createCartOrder(payload: CartCheckoutPayload, token: string): Promise<CartCheckoutResponse> {
  return apiFetch<CartCheckoutResponse>("/orders/cart-checkout", {
    method: "POST",
    token,
    body: JSON.stringify(payload)
  });
}

export async function initiatePayment(orderId: string | number, provider: string, token: string): Promise<PaymentInitResponse> {
  return apiFetch<PaymentInitResponse>("/payments/initiate", {
    method: "POST",
    token,
    body: JSON.stringify({ order_id: orderId, provider })
  });
}

export async function repayOrder(orderId: string | number, provider: "manual" | "phonepe", token: string): Promise<PaymentInitResponse | { provider: "manual"; order_id: string | number; status: string; extra?: Record<string, unknown> }> {
  return apiFetch("/payments/repay", {
    method: "POST",
    token,
    body: JSON.stringify({ order_id: orderId, provider })
  });
}

export async function verifyPayment(gatewayOrderId: string): Promise<PaymentVerifyResponse> {
  return apiFetch<PaymentVerifyResponse>("/payments/verify", {
    method: "POST",
    body: JSON.stringify({ gateway_order_id: gatewayOrderId })
  });
}

export async function requestProviderQuote(input: QuoteRequest): Promise<QuoteResult> {
  const formData = new FormData();
  formData.append("file", input.file);
  formData.append("request_id", `web-react-${Date.now()}`);
  formData.append("material", input.material.toLowerCase());
  formData.append("quality", mapNozzleToQuality(input.nozzle));
  formData.append("color", input.color);
  formData.append("infill", mapInfillToProviderLevel(input.infill));
  formData.append("support", input.support);
  formData.append("infill_pattern", input.infillPattern);
  formData.append("support_style", input.supportStyle);
  formData.append("top_bottom_layers", String(input.topBottomLayers));
  formData.append("extra_requests", input.extraRequests);

  const userQ = input.userId ? `?user_id=${encodeURIComponent(String(input.userId))}` : "";
  const raw = await apiFetch<Record<string, unknown>>(`/quotes/provider-quote${userQ}`, {
    method: "POST",
    body: formData
  });

  const payload =
    (typeof raw.quote === "object" && raw.quote ? raw.quote as Record<string, unknown> : null) ||
    (typeof raw.data === "object" && raw.data ? raw.data as Record<string, unknown> : null) ||
    (typeof raw.result === "object" && raw.result ? raw.result as Record<string, unknown> : null) ||
    raw;

  const printDetails = typeof payload.print_details === "object" && payload.print_details
    ? payload.print_details as Record<string, unknown>
    : typeof raw.print_details === "object" && raw.print_details
      ? raw.print_details as Record<string, unknown>
      : {};

  const firstFinite = (...values: unknown[]) => {
    for (const value of values) {
      const num = Number(value);
      if (Number.isFinite(num)) return num;
    }
    return undefined;
  };

  const price = firstFinite(
    payload.base_cost,
    raw.base_cost,
    payload.price,
    raw.price,
    payload.total_price,
    raw.total_price,
    payload.final_price,
    raw.final_price,
    payload.quote_price,
    raw.quote_price
  ) ?? 0;

  const volumeCm3 = firstFinite(
    printDetails.volume_cm3,
    payload.volume_cm3,
    raw.volume_cm3,
    payload.volume,
    raw.volume
  );

  const filamentGrams = firstFinite(
    printDetails.filament_g,
    payload.filament_g,
    raw.filament_g,
    payload.weight,
    raw.weight,
    payload.weight_grams,
    raw.weight_grams
  );

  return {
    filename: input.file.name,
    price: price * input.quantity,
    material: input.material,
    color: input.color,
    model_file_id: firstFinite(payload.model_file_id, raw.model_file_id) ?? null,
    stl_file:
      (typeof payload.model_file_url === "string" ? payload.model_file_url : null) ||
      (typeof raw.model_file_url === "string" ? raw.model_file_url : null),
    volume: volumeCm3,
    weight: filamentGrams,
    quote_options: {
      material: input.material,
      color: input.color,
      infill: input.infill,
      support: input.support,
      infill_pattern: input.infillPattern,
      nozzle: input.nozzle,
      support_style: input.supportStyle,
      top_bottom_layers: input.topBottomLayers,
      extra_requests: input.extraRequests,
      quantity: input.quantity
    },
    raw: payload
  };
}

function mapNozzleToQuality(nozzle: string) {
  return nozzle === "0.6" ? "draft" : "standard";
}

function mapInfillToProviderLevel(infill: string) {
  if (infill === "solid") return "solid";
  if (infill === "high") return "high";
  return "standard";
}

export function formatCurrency(value: number | string | undefined): string {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(amount);
}

export function formatDate(value?: string): string {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
}

export function getUserId(user: User | null): string | number | null {
  return user?.id || user?.user_id || null;
}
